import { createFileRoute } from '@tanstack/react-router'
import { Boton } from '@/ui/Boton'
import { Banner } from '@/ui/Banner'
import { EstadoError } from '@/ui/EstadoError'
import { EstadoVacio } from '@/ui/EstadoVacio'
import { Esqueleto, FilasEsqueleto, LineaEsqueleto } from '@/ui/Esqueleto'
import { LineaDeTiempo, type EventoLinea } from '@/ui/LineaDeTiempo'
import { PilaDeAvisos } from '@/ui/Avisos'
import { traducirErrorApi, traducirRechazo } from '@/dominio/documentos/errores'
import { ErrorApi } from '@/api/errores'
import { Etiqueta, Rejilla, Seccion, Tarjeta } from './-componentes'

export const Route = createFileRoute('/sistema/retroalimentacion')({
  component: CatalogoRetroalimentacion,
})

const EVENTOS: readonly EventoLinea[] = [
  { id: '1', etiqueta: 'Emitido', momento: '2026-09-21T18:32:07Z', detalle: 'por Ana M.', tono: 'marca' },
  { id: '2', etiqueta: 'Firmado', momento: '2026-09-21T18:32:08Z', tono: 'marca' },
  {
    id: '3',
    etiqueta: 'Enviado a DGII',
    momento: '2026-09-21T18:32:09Z',
    detalle: 'TrackId a7f3-9b2c',
    tono: 'marca',
  },
  { id: '4', etiqueta: 'En proceso', momento: '2026-09-21T18:32:09Z', tono: 'ambar' },
  {
    id: '5',
    etiqueta: 'Aceptado',
    momento: '2026-09-21T18:32:11Z',
    tono: 'verde',
    crudo: '{"codigo":"1","mensaje":"Aceptado","trackId":"a7f3-9b2c","fecha":"21-09-2026 14:32:11"}',
  },
  { id: '6', etiqueta: 'Acuse recibido', momento: '2026-09-21T18:35:44Z', tono: 'verde' },
  {
    id: '7',
    etiqueta: 'Aprobación comercial — opcional · corte del 15 de octubre',
    soloNota: true,
  },
]

const RECHAZOS = [
  traducirRechazo('rnc_receptor_invalido', { nombreContraparte: 'Ferretería Sánchez' }),
  traducirRechazo('secuencia_fuera_de_rango', { tipo: '31', finDeRango: '50,000' }),
  traducirRechazo('firma_invalida'),
  traducirRechazo('encf_duplicado'),
  traducirErrorApi(new ErrorApi({ mensaje: 'timeout', clase: 'red', estado: 0 })),
  traducirErrorApi(new ErrorApi({ mensaje: 'upstream', clase: 'servidor', estado: 503 })),
]

function CatalogoRetroalimentacion() {
  return (
    <div className="flex flex-col gap-8">
      {/* Vacíos --------------------------------------------------------- */}
      <Seccion
        titulo="Estados vacíos"
        nota="Tres tipos que nunca se confunden. Sin ilustraciones, sin cajas vacías de 400px de alto, sin «¡Ups!». El tercero es el más importante: cuando no hay pendientes eso es una buena noticia y debe ocupar poco espacio."
      >
        <Rejilla columnas={3}>
          <Tarjeta className="p-0">
            <div className="border-b border-gris-300 px-3 py-2">
              <Etiqueta>Primera vez</Etiqueta>
            </div>
            <EstadoVacio
              variante="primera-vez"
              mensaje="Aún no has emitido facturas."
              accion={<Boton variante="primario">Nueva factura</Boton>}
            />
          </Tarjeta>

          <Tarjeta className="p-0">
            <div className="border-b border-gris-300 px-3 py-2">
              <Etiqueta>Filtro sin resultados</Etiqueta>
            </div>
            <EstadoVacio
              variante="sin-resultados"
              mensaje="Ninguna factura entre el 1 y el 15 de octubre."
              accion={<Boton variante="secundario">Limpiar filtros</Boton>}
            />
          </Tarjeta>

          <Tarjeta className="p-0">
            <div className="border-b border-gris-300 px-3 py-2">
              <Etiqueta>Todo resuelto</Etiqueta>
            </div>
            <EstadoVacio variante="resuelto" mensaje="Sin pendientes." />
          </Tarjeta>
        </Rejilla>
      </Seccion>

      {/* Carga ---------------------------------------------------------- */}
      <Seccion
        titulo="Carga"
        nota="Esqueletos, no ruedas girando. Las filas grises tienen el alto correcto, así que la pantalla no salta al llegar los datos. La navegación y los filtros aparecen primero: nunca una pantalla en blanco."
      >
        <Rejilla columnas={2}>
          <Tarjeta className="p-0">
            <div className="border-b border-gris-300 px-3 py-2">
              <Etiqueta>Filas de tabla · densidad normal</Etiqueta>
            </div>
            <div data-densidad="normal">
              <table className="w-full border-collapse">
                <caption className="solo-lectores">Ejemplo de esqueleto de tabla</caption>
                <tbody>
                  <FilasEsqueleto filas={6} columnas={5} />
                </tbody>
              </table>
            </div>
          </Tarjeta>

          <Tarjeta className="flex flex-col gap-3">
            <Etiqueta>Bloques</Etiqueta>
            <Esqueleto className="h-6 w-1/3" />
            <LineaEsqueleto ancho="w-full" />
            <LineaEsqueleto ancho="w-5/6" />
            <LineaEsqueleto ancho="w-2/3" />
            <Esqueleto className="h-[var(--alto-control)] w-32" />
          </Tarjeta>
        </Rejilla>
      </Seccion>

      {/* Errores -------------------------------------------------------- */}
      <Seccion
        titulo="Errores de DGII traducidos"
        nota="Ningún mensaje crudo de DGII llega al usuario. Cada código se mapea a tres cosas: qué pasó, por qué, y qué hacer. El código original queda accesible, plegado, para soporte. Los modales para errores están prohibidos."
      >
        <div className="flex flex-col gap-2">
          {RECHAZOS.map((mensaje) => (
            <Banner
              key={mensaje.titulo}
              tono={mensaje.tono}
              titulo={mensaje.titulo}
              detalleTecnico={mensaje.detalleTecnico ?? '{"codigo":"DGII-0042","detalle":"…"}'}
              accion={
                mensaje.accion ? (
                  <Boton variante="secundario" tamano="menor">
                    {mensaje.accion.etiqueta}
                  </Boton>
                ) : undefined
              }
            >
              {mensaje.contexto}
            </Banner>
          ))}

          <Banner
            tono="verde"
            titulo="Aceptada."
            accion={
              <Boton variante="secundario" tamano="menor">
                Ver detalle
              </Boton>
            }
          >
            DGII registró una observación menor; la factura es válida.
          </Banner>

          <Banner tono="rojo" titulo="No puedes facturar: tu certificado venció el 3 de octubre.">
            Este es el único caso del producto donde bloquear al usuario es lo correcto, y el banner
            no se puede cerrar.
          </Banner>
        </div>
      </Seccion>

      <Seccion
        titulo="Error de un componente de datos"
        nota="El cuarto estado de la tabla. Reintentar es la acción por defecto; el detalle técnico queda plegado."
      >
        <Tarjeta className="p-0">
          <EstadoError
            titulo="No pudimos cargar los documentos."
            contexto="El servidor respondió con un error. Tu trabajo no se perdió."
            detalleTecnico='{"estado":503,"codigo":"upstream_indisponible","trackId":"a7f3-9b2c"}'
            onReintentar={() => undefined}
          />
        </Tarjeta>
      </Seccion>

      {/* Avisos --------------------------------------------------------- */}
      <Seccion
        titulo="Notificación de fondo"
        nota="Abajo a la derecha, persistente hasta leerse. Un «aceptado» no merece notificación: es lo esperado. Solo se avisa de lo que exige acción."
      >
        {/* `translateZ(0)` crea el bloque contenedor para que la pila —que en
            la aplicación es `fixed` sobre la ventana— se quede dentro de esta
            caja de muestra en vez de flotar sobre el catálogo. */}
        <div className="relative h-40 overflow-hidden rounded-md border border-gris-300 bg-gris-50 [transform:translateZ(0)]">
          <div className="absolute inset-0">
            <PilaDeAvisos
              avisos={[
                {
                  id: 'a1',
                  tono: 'rojo',
                  titulo: 'DGII rechazó E31…0846.',
                  detalle: 'El RNC del cliente no existe en DGII.',
                },
                {
                  id: 'a2',
                  tono: 'ambar',
                  titulo: '12 documentos sin transmitir.',
                  detalle: 'El más viejo lleva 9 h de 72.',
                },
              ]}
              onDescartar={() => undefined}
            />
          </div>
        </div>
      </Seccion>

      {/* Línea de tiempo ------------------------------------------------ */}
      <Seccion
        titulo="Línea de tiempo"
        nota="La vida del documento, con las horas alineadas en monoespaciada. La respuesta cruda de DGII está disponible pero plegada: el usuario normal no la abre nunca y soporte la necesita siempre."
      >
        <Tarjeta className="max-w-[520px]">
          <LineaDeTiempo eventos={EVENTOS} />
        </Tarjeta>
      </Seccion>
    </div>
  )
}
